"use client";

import {
	AnimatePresence,
	domAnimation,
	LazyMotion,
	m,
	useReducedMotion,
} from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
	clearTransition,
	useBlogTransition,
} from "@/lib/stores/blog-transition-store";

const MORPH_DURATION = 0.6;
const MORPH_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

export function BlogTransitionOverlay() {
	const data = useBlogTransition();
	const prefersReducedMotion = useReducedMotion();
	const [phase, setPhase] = useState<"idle" | "morphing" | "done">("idle");
	const [viewport, setViewport] = useState<{ w: number; h: number } | null>(
		null,
	);

	useEffect(() => {
		if (!data) {
			setPhase("idle");
			return;
		}

		if (prefersReducedMotion) {
			clearTransition();
			return;
		}

		// Capture viewport at morph start (client-only, not during render).
		setViewport({ w: window.innerWidth, h: window.innerHeight });
		setPhase("morphing");

		const timer = setTimeout(
			() => {
				setPhase("done");
			},
			MORPH_DURATION * 1000 + 400,
		);

		return () => clearTimeout(timer);
	}, [data, prefersReducedMotion]);

	// Once phase is "done", wait for the page to be painted then clear
	useEffect(() => {
		if (phase !== "done") return;

		const timer = setTimeout(() => {
			clearTransition();
			setPhase("idle");
		}, 300);

		return () => clearTimeout(timer);
	}, [phase]);

	const isVisible = phase === "morphing" || phase === "done";

	// Precompute the starting transform so the banner-sized element (final
	// state) begins scaled/translated onto the source card rect. Animating
	// transform + borderRadius keeps the morph on the GPU compositor, unlike
	// animating top/left/width/height which forces layout every frame.
	const morph = useMemo(() => {
		if (!data || !viewport) return null;
		const rect = data.cardRect;
		const finalWidth = viewport.w; // 100vw target
		const finalHeight = viewport.h * 0.7; // 70vh target
		const cardHeight = rect.width * 0.625; // 16:10 aspect
		return {
			x0: rect.left,
			y0: rect.top,
			scaleX0: rect.width / finalWidth,
			scaleY0: cardHeight / finalHeight,
		};
	}, [data, viewport]);

	return (
		<LazyMotion features={domAnimation}>
			<AnimatePresence>
				{isVisible && data && morph && (
					<m.div
						key="blog-transition-overlay"
						className="fixed inset-0 z-40 pointer-events-none"
						initial={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3, ease: "easeOut" }}
						aria-hidden="true"
					>
						{/* Dark background fade */}
						<m.div
							className="absolute inset-0 bg-zinc-950"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: MORPH_DURATION * 0.6, ease: "easeOut" }}
						/>

						{/* Cover image morphing from card to full banner. The element
					    is laid out at its final banner size, then transform-scaled
					    down onto the card rect and animated back to identity. */}
						<m.div
							className="absolute top-0 left-0 overflow-hidden"
							style={{
								width: "100vw",
								height: "70vh",
								transformOrigin: "top left",
							}}
							initial={{
								x: morph.x0,
								y: morph.y0,
								scaleX: morph.scaleX0,
								scaleY: morph.scaleY0,
								borderRadius: 16,
							}}
							animate={{
								x: 0,
								y: 0,
								scaleX: 1,
								scaleY: 1,
								borderRadius: 0,
							}}
							transition={{ duration: MORPH_DURATION, ease: MORPH_EASE }}
						>
							{/* biome-ignore lint/performance/noImgElement: Raw img needed for Framer Motion position animation - next/image doesn't support animated position/size */}
							<img
								src={data.coverImage || "/placeholders/blog-cover.svg"}
								alt=""
								className="w-full h-full object-cover"
							/>
							{/* Gradient overlay on image */}
							<m.div
								className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/30"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{
									duration: MORPH_DURATION * 0.8,
									delay: MORPH_DURATION * 0.2,
									ease: "easeOut",
								}}
							/>
						</m.div>
					</m.div>
				)}
			</AnimatePresence>
		</LazyMotion>
	);
}
